// Used in many components

// Angular
import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// RXJS/Store
import * as fromNotificationsStore from "#core/notifications/store";
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as profileStore from "#login/store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { OpenSidePanelRightHelper } from "#core/helpers";
import { PublicService } from "@carnegie-learning/data-access-angular-passport";
import { PPUser } from "cli-types";

@Component({
    selector: "profile-messaging-container",
    templateUrl: "./profile-messaging-container.component.html",
    styleUrls: [
        "./profile-messaging-container.component.scss",
        "../../../../core/styles/shared-styles.scss",
    ],
    standalone: false
})
export class ProfileMessagingContainerComponent
    implements OnInit, OnDestroy, OnChanges
{
    private ngUnsubscribe = new Subject();
    userProfile?: PPUser;
    unreadNotificationsCount?: number;
    public frontendBuildNumber: number;
    public apiBuildNumber: number;
    profileName = "";
    localTimezoneDisplay = "";
    localTimezone = "";
    timezoneMismatch = false;

    @Input() radiusType = "circle";
    @Input() schoolTimezone = "";
    @Input() viewAsStudent = false;

    constructor(
        private profile_store: Store<profileStore.UserState>,
        private course_store: Store<fromCoursesStore.CoursesState>,
        private router: Router,
        private route: ActivatedRoute,
        private sidePanelRightHelper: OpenSidePanelRightHelper,
        private notifications_store: Store<fromNotificationsStore.NotificationsState>,
        private publicService: PublicService
    ) {}

    ngOnInit(): void {
        this.frontendBuildNumber = (<any>window).buildNumber;

        this.publicService
            .getVersion()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response) => {
                this.apiBuildNumber = response.version;
            });

        this.profile_store
            .pipe(select(profileStore.getUserProfile))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user: PPUser) => {
                if (user) {
                    this.userProfile = user;
                    this.localTimezoneDisplay = user?.timezone?.replace(
                        "_",
                        " "
                    );
                    this.localTimezone = user.timezone;
                    if (this.localTimezone && this.schoolTimezone) {
                        this.timezoneMismatch =
                            this.localTimezone !== this.schoolTimezone;
                    }
                    if (user.firstName) {
                        this.profileName =
                            user.firstName +
                            " " +
                            user.lastName.charAt(0) +
                            ".";
                    } else {
                        this.profileName = "";
                    }
                }
            });

        if (
            this.userProfile?.unreadNotificationsCount ||
            this.userProfile?.unreadNotificationsCount === 0
        ) {
            this.unreadNotificationsCount =
                this.userProfile?.unreadNotificationsCount;
        }

        this.profile_store
            .pipe(select(profileStore.getUnreadNotificationsCount))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((unreadNotificationsCount) => {
                if (
                    (unreadNotificationsCount && !this.viewAsStudent) ||
                    unreadNotificationsCount === 0
                ) {
                    this.unreadNotificationsCount = unreadNotificationsCount;
                }
            });
    }

    ngOnChanges() {
        // Determine if your timezone is different from your school's timezone.
        if (this.localTimezone && this.schoolTimezone) {
            this.timezoneMismatch = this.localTimezone !== this.schoolTimezone;
        }
    }

    // Open Messages
    onToggleSidePanelRight() {
        if (!this.viewAsStudent) {
            this.notifications_store.dispatch(
                new fromNotificationsStore.OpenMessagesPanel(true)
            );
        }
    }

    // Edit Profile
    editProfile() {
        this.profile_store
            .pipe(select(profileStore.isStudent))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isStudent) => {
                if (isStudent) {
                    this.router.navigate(["/student/profile/edit-student"], {
                        relativeTo: this.route,
                    });
                } else {
                    this.router.navigate(["teacher/profile/edit"]);
                }
            });
    }

    getHelpCenterUrl() {
        if (this.userProfile) {
            const roleLevel = this.userProfile.roleLevel;
            if (roleLevel === 5) {
                // If Student - https://www.carnegielearning.com/help-center/
                return "https://www.carnegielearning.com/help-center/";
            } else if (roleLevel === 4) {
                // If Teacher - https://www.carnegielearning.com/help-center/
                return "https://www.carnegielearning.com/help-center/";
            }
        }
    }

    // Logout
    logout(signout: boolean) {
        if (this.userProfile && this.userProfile.roleLevel < 5) {
            // Only non-students we set to null
            this.course_store.dispatch(
                fromCoursesStore.setSelectedCourse({ payload: null })
            );
        }

        this.profile_store.dispatch(profileStore.userLogout({ signout }));
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
